import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { FinalCTA } from "@/pages/Home";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Seo from "@/components/Seo";
import { mediaUrl } from "@/lib/api";
import RichText from "@/components/RichText";

export default function InsightDetail() {
  const { t, isAr } = useLang();
  const { slug } = useParams();
  const { data: a, isLoading, isError } = useQuery({
    queryKey: ["insight", slug],
    queryFn: () => api.get(`/insights/${slug}`).then((r) => r.data),
  });

  if (isLoading) return <div className="pt-40 container-x text-muted-text">{t("Loading…")}</div>;
  if (isError || !a) return (
    <div className="pt-40 pb-40 container-x text-center">
      <h1 className="text-3xl font-light text-ivory">{t("Article not found.")}</h1>
      <Link to="/insights" className="btn-gold mt-8">{t("Back to Insights")}</Link>
    </div>
  );
  const title = isAr && a.title_ar ? a.title_ar : a.title;
  const excerpt = isAr && a.excerpt_ar ? a.excerpt_ar : a.excerpt;
  const content = isAr && a.content_ar ? a.content_ar : a.content;

  return (
    <>
      <Seo
        title={a.seo_title ? `${a.seo_title} — Intersect Holding` : `${title} — Intersect Holding`}
        description={(a.seo_description || excerpt || (content || "").replace(/<[^>]*>/g, " ").slice(0, 160) || title)}
        path={`/insights/${slug}`}
        image={a.hero_image ? mediaUrl(a.hero_image) : undefined}
        type="article"
        lang={isAr ? "ar" : "en"}
      />
      <article className="pt-36 pb-24">
        <div className="max-w-[800px] mx-auto px-6">
          <Link to="/insights" className="link-underline text-sm tracking-wide uppercase mb-10"><ArrowLeft size={16} strokeWidth={1.5} /> {t("Insights")}</Link>
          <Reveal><div className="flex items-center gap-3 text-xs tracking-wide uppercase text-primary-gold mt-8 mb-6"><span>{t(a.category)}</span><span className="text-white/20">·</span><span className="text-muted-text">{a.reading_time}</span></div></Reveal>
          <Reveal delay={0.1}><h1 className="text-4xl md:text-5xl font-light tracking-tight text-ivory leading-tight">{title}</h1></Reveal>
          <Reveal delay={0.15}><p className="mt-6 text-sm text-muted-text">{a.author} · {a.date}</p></Reveal>
        </div>
        {a.hero_image && (
          <Reveal delay={0.2}>
            <div className="max-w-[1100px] mx-auto px-6 mt-12"><img src={mediaUrl(a.hero_image)} alt={title} className="w-full aspect-[16/8] object-cover" /></div>
          </Reveal>
        )}
        <div className="max-w-[800px] mx-auto px-6 mt-14">
          {excerpt && <p className="serif-italic text-2xl md:text-3xl text-luxury-gold leading-snug mb-10">{excerpt}</p>}
          <RichText html={content} className="text-lg text-soft-stone" />
        </div>
      </article>
      <FinalCTA />
    </>
  );
}
